import moment from 'moment';

/** Fixed past window: today − 3 … today − 1 (today is the 4th slot). */
export const GUEST_MENU_PAST_DAYS = 3;

/** Doc default / API fallback when `guest_menu_size` is missing or invalid. */
export const GUEST_MENU_SIZE_DEFAULT = 7;

/** Values below this are clamped to 7. */
export const GUEST_MENU_SIZE_MIN = 7;

/** Values above this enable unbounded future browsing. */
export const GUEST_MENU_INFINITE_THRESHOLD = 30;

/** How many future days to add when the guest nears the infinite-window edge. */
export const GUEST_MENU_INFINITE_EXTEND_CHUNK = 7;

/** Initial future span when infinite mode starts (matches the 7-day default window). */
export const GUEST_MENU_INFINITE_INITIAL_FUTURE = GUEST_MENU_SIZE_DEFAULT - GUEST_MENU_PAST_DAYS - 1;

/** @deprecated Use {@link GUEST_MENU_PAST_DAYS}. */
export const GUEST_DISPLAY_MENU_PAST_DAYS = GUEST_MENU_PAST_DAYS;

/** @deprecated Use {@link GUEST_MENU_INFINITE_INITIAL_FUTURE} for the legacy 7-day window. */
export const GUEST_DISPLAY_MENU_FUTURE_DAYS = GUEST_MENU_INFINITE_INITIAL_FUTURE;

export type GuestMenuSizeMode = 'finite' | 'infinite';

export type ResolvedGuestMenuSize = {
  /** Clamped numeric config (7 … ∞ threshold+). */
  effective_size: number;
  mode: GuestMenuSizeMode;
  past_days: number;
  /** Count of future days after today (offsets +1 … +future_days). */
  future_days: number;
};

export function parseGuestMenuSizeConfigValue(value: unknown): number | undefined {
  if (value === undefined || value === null || String(value).trim() === '') {
    return undefined;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return undefined;
  }
  return Math.floor(parsed);
}

/** Normalize raw `guest_menu_size` from app_config (min 7; >30 → infinite). */
export function resolveGuestMenuSize(raw?: number | null): ResolvedGuestMenuSize {
  const parsed = parseGuestMenuSizeConfigValue(raw) ?? GUEST_MENU_SIZE_DEFAULT;
  const effective_size = Math.max(GUEST_MENU_SIZE_MIN, parsed);

  if (effective_size > GUEST_MENU_INFINITE_THRESHOLD) {
    return {
      effective_size,
      mode: 'infinite',
      past_days: GUEST_MENU_PAST_DAYS,
      future_days: GUEST_MENU_INFINITE_INITIAL_FUTURE,
    };
  }

  const future_days = Math.max(0, effective_size - GUEST_MENU_PAST_DAYS - 1);
  return {
    effective_size,
    mode: 'finite',
    past_days: GUEST_MENU_PAST_DAYS,
    future_days,
  };
}

export function guestDisplayMenuTodayIso(): string {
  moment.locale('en');
  return moment().startOf('day').format('YYYY-MM-DD');
}

/**
 * Build guest browse dates: `past_days` before today, today, then `future_days` after today.
 * Infinite mode passes an extended `future_days` as the window grows.
 */
export function buildGuestDisplayMenuDates(
  config?: ResolvedGuestMenuSize | number | null,
  reference_date?: string,
  future_extent?: number,
): string[] {
  const resolved =
    typeof config === 'number' || config == null
      ? resolveGuestMenuSize(typeof config === 'number' ? config : undefined)
      : config;
  const future_days =
    resolved.mode === 'infinite'
      ? Math.max(future_extent ?? resolved.future_days, resolved.future_days)
      : resolved.future_days;

  moment.locale('en');
  const anchor = reference_date
    ? moment(reference_date, 'YYYY-MM-DD', true).startOf('day')
    : moment().startOf('day');
  const dates: string[] = [];
  for (let offset = -resolved.past_days; offset <= future_days; offset++) {
    dates.push(anchor.clone().add(offset, 'day').format('YYYY-MM-DD'));
  }
  return dates;
}

/** Offset in whole days from today (`YYYY-MM-DD` ASCII). */
export function guestMenuDayOffsetFromToday(date_ascii: string): number | null {
  moment.locale('en');
  const target = moment(date_ascii, 'YYYY-MM-DD', true).startOf('day');
  if (!target.isValid()) {
    return null;
  }
  const today = moment().startOf('day');
  return target.diff(today, 'days');
}

/**
 * Compute the future-day extent needed so `date_ascii` is selectable,
 * keeping a one-day buffer beyond the target when extending.
 */
export function resolveGuestInfiniteFutureExtent(
  date_ascii: string,
  current_future_days: number,
): number {
  const offset = guestMenuDayOffsetFromToday(date_ascii);
  if (offset == null) {
    return current_future_days;
  }
  if (offset <= current_future_days - 1) {
    return current_future_days;
  }
  if (offset >= current_future_days) {
    return offset + 1;
  }
  return current_future_days + GUEST_MENU_INFINITE_EXTEND_CHUNK;
}
